import React, { useState, useEffect } from "react";
import { Pencil, Trash2, X, ChevronLeft, Plus, Search, Download, MapPin, Edit } from "lucide-react";
import apiService from "../config";
import { useLanguage } from '../StoreContext/LanguageContext';
import DownloadModal from '../Components/Download/DownloadModal';
import DownloadService from "../Components/Download/DownloadService";

// Custom Button Component
const Button = ({
  children,
  onClick,
  type = "button",
  variant = "primary",
  className = "",
}) => {
  const baseClasses = "px-4 py-2 rounded text-white font-medium shadow";
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700",
    outline:
      "bg-transparent border border-blue-600 text-blue-600 hover:bg-blue-50",
    danger: "bg-red-600 hover:bg-red-700",
    success: "bg-green-600 hover:bg-green-700",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

// Custom Switch Component
const Switch = ({ checked, onChange, disabled = false }) => {
  return (
    <label
      className={`inline-flex items-center cursor-pointer ${disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div
        className={`w-11 h-6 bg-gray-300 rounded-full p-1 transition ${checked ? "bg-green-500" : "bg-gray-400"
          }`}
      >
        <div
          className={`w-4 h-4 bg-white rounded-full shadow transform transition ${checked ? "translate-x-5" : ""
            }`}
        />
      </div>
    </label>
  );
};

const LandTypeManagement = ({ onCancel, onBack }) => {
  const { language, translations } = useLanguage();
  const [landTypes, setLandTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formVisible, setFormVisible] = useState(false);
  const [formMode, setFormMode] = useState("add"); // 'add' or 'edit'
  const [landRates, setLandRates] = useState([]);
  const [areaUnits, setAreaUnits] = useState([]);
  const [acts, setActs] = useState([]);

  const [currentData, setCurrentData] = useState({
    land_type_id: 0,
    type_code: "",
    type_name_en: "",
    type_name_mr: "",
    assessment_type: "",
    revenue_category: "",
    is_agricultural: false,
    irrigation_type: "",
    crop_pattern: "",
    is_non_agricultural: false,
    na_category: "",
    na_purpose: "",
    na_permission_required: false,
    is_forest: false,
    forest_category: "",
    is_government: false,
    govt_department: "",
    govt_purpose: "",
    is_gaothan: false,
    is_water_body: false,
    water_body_type: "",
    is_sez: false,
    is_tribal_area: false,
    is_crz: false,
    is_heritage: false,
    land_rate_multiplier: 1.0,
    development_potential: "",
    transfer_restrictions: "",
    ceiling_applicable: false,
    tenancy_applicable: false,
    parent_type_id: null,
    classification_level: "",
    description_en: "",
    description_mr: "",
    legal_reference: "",
    display_order: 0,
    is_active: true,
  });

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // 'all', 'active', 'inactive'
  const [typeFilter, setTypeFilter] = useState("all"); // 'all', 'agricultural', 'forest', 'government', etc.

  //download
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
  setCurrentPage(1);
}, [searchTerm, activeFilter, typeFilter]);


  useEffect(() => {
    const fetchLandTypes = async () => {
      try {
        const response = await apiService.getLandTypes();
        setLandTypes(response.data || []);
      } catch (error) {
        console.error("Failed to fetch land types:", error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLandTypes();
  }, []);

  useEffect(() => {
    const fetchLandRates = async () => {
      try {
        const res = await apiService.getLandRates();
        setLandRates(res.data || []);
      } catch (err) {
        console.error("Failed to load land rates:", err.message);
      }
    };

    fetchLandRates();
  }, []);

  useEffect(() => {
    const fetchAreaUnits = async () => {
      try {
        const res = await apiService.getAreaUnits();
        setAreaUnits(res.data || []);
      } catch (err) {
        console.error("Failed to load area units:", err.message);
      }
    };

    fetchAreaUnits();
  }, []);

  useEffect(() => {
    const fetchActs = async () => {
      try {
        const res = await apiService.getActs();
        setActs(res.data || []);
      } catch (err) {
        console.error("Failed to load acts:", err.message);
      }
    };

    fetchActs();
  }, []);

  const toggleStatus = (id) => {
    setLandTypes((prev) =>
      prev.map((item) =>
        item.land_type_id === id
          ? { ...item, is_active: !item.is_active }
          : item
      )
    );
  };

  // Define columns for download
  const downloadColumns = [
    // { header: 'S.No.', value: 'index' },
    { header: translations[language]?.CODE || ' CODE', value: 'type_code' },
    // { header: translations[language]?.NAME_EN_MR || 'NAME (EN/MR)', value: 'type_name_mr' },
    { header: translations[language]?.Name_English || 'Name (EN)', value: 'type_name_en' },
    // { header: translations[language]?.DESCRIPTION || 'DESCRIPTION', value: 'description_en' },
    { header: translations[language]?.CATEGORY || 'CATEGORY', value: 'revenue_category' },
    // { header: translations[language]?.PROPERTIES || 'PROPERTIES', value: 'stateCode' },
    
    // { header: translations[language]?.STATUS || 'STATUS', value: 'is_active' },
  ];
  const handleDownload = () => {
    if (currentData.length === 0) {
      alert('No data available to download');
      return;
    }
    setShowDownloadModal(true);
  };
  const handleDelete = async (id) => {
    // Make sure we have a valid ID
    if (!id) {
      console.error("No ID provided for delete operation");
      alert("Error: No ID provided for delete operation");
      return;
    }

    console.log("Attempting to delete land type with ID:", id); // Debug log

    if (window.confirm("Are you sure you want to delete this land type?")) {
      try {
        await apiService.deleteLandType(id);
        const refreshed = await apiService.getLandTypes();
        setLandTypes(refreshed.data || []);
        alert("Land type deleted successfully!");
      } catch (error) {
        console.error("Delete failed:", error.message);
        alert("Delete failed: " + error.message);
      }
    }
  };

  // const handleEdit = (data) => {
  //   setFormMode("edit");
  //   setCurrentData({ ...data });
  //   setFormVisible(true);
  // };

  const handleEdit = (data) => {
  setFormMode("edit");
  setCurrentData({
    ...data,
    area_unit_id: data.AREA_UNIT || null,
    act_id: data.legal_reference || null,
  });
  setFormVisible(true);
};


  const handleAdd = () => {
    setFormMode("add");
    setCurrentData({
      land_type_id:
        Math.max(...landTypes.map((item) => item.land_type_id), 0) + 1,
      type_code: "",
      type_name_en: "",
      type_name_mr: null, // Changed from '' to null
      assessment_type: null, // Changed from '' to null
      revenue_category: null, // Changed from '' to null
      is_agricultural: false,
      irrigation_type: null, // Changed from '' to null
      crop_pattern: null, // Changed from '' to null
      is_non_agricultural: false,
      na_category: null, // Changed from '' to null
      na_purpose: null, // Changed from '' to null
      na_permission_required: false,
      is_forest: false,
      forest_category: null, // Changed from '' to null
      is_government: false,
      govt_department: null, // Changed from '' to null
      govt_purpose: null, // Changed from '' to null
      is_gaothan: false,
      is_water_body: false,
      water_body_type: null, // Changed from '' to null
      is_sez: false,
      is_tribal_area: false,
      is_crz: false,
      is_heritage: false,
      land_rate_multiplier: 1.0, // FIXED: Changed from ' ' to 1.0
      development_potential: null, // Changed from '' to null
      transfer_restrictions: null, // Changed from '' to null
      ceiling_applicable: false,
      tenancy_applicable: false,
      parent_type_id: null,
      classification_level: "",
      description_en: null, // Changed from '' to null
      description_mr: null, // Changed from '' to null
      legal_reference: null, // Changed from '' to null
      display_order: "",
      is_active: "",
    });
    setFormVisible(true);
  };
  // const handleFormChange = (field, value) => {
  //   setCurrentData((prev) => ({ ...prev, [field]: value }));
  // };

  // FIXED handleFormChange function
  const handleFormChange = (field, value) => {
    setCurrentData((prev) => ({
      ...prev,
      [field]: value === "" ? null : value, // Convert empty strings to null
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      // Create a clean payload object matching the Postman structure
      const payload = {
        type_code: currentData.type_code.trim(),
        type_name_en: currentData.type_name_en.trim()
      };

      // Only add optional fields if they have values
      if (currentData.type_name_mr && currentData.type_name_mr.trim()) {
        payload.type_name_mr = currentData.type_name_mr.trim();
      }

      if (currentData.assessment_type && currentData.assessment_type.trim()) {
        payload.assessment_type = currentData.assessment_type.trim();
      }

      if (currentData.revenue_category && currentData.revenue_category.trim()) {
        payload.revenue_category = currentData.revenue_category.trim();
      }

      // Add boolean fields - convert to 0/1 for database compatibility
      payload.is_agricultural = currentData.is_agricultural ? 1 : 0;
      payload.is_non_agricultural = currentData.is_non_agricultural ? 1 : 0;
      payload.is_forest = currentData.is_forest ? 1 : 0;
      payload.is_government = currentData.is_government ? 1 : 0;
      payload.is_gaothan = currentData.is_gaothan ? 1 : 0;
      payload.is_water_body = currentData.is_water_body ? 1 : 0;
      payload.is_sez = currentData.is_sez ? 1 : 0;
      payload.is_tribal_area = currentData.is_tribal_area ? 1 : 0;
      payload.is_crz = currentData.is_crz ? 1 : 0;
      payload.is_heritage = currentData.is_heritage ? 1 : 0;
      payload.na_permission_required = currentData.na_permission_required ? 1 : 0;
      payload.ceiling_applicable = currentData.ceiling_applicable ? 1 : 0;
      payload.tenancy_applicable = currentData.tenancy_applicable ? 1 : 0;
      payload.is_active = currentData.is_active ? 1 : 0;

      // Add conditional fields based on selections
      if (currentData.is_agricultural) {
        if (currentData.irrigation_type && currentData.irrigation_type.trim()) {
          payload.irrigation_type = currentData.irrigation_type.trim();
        }
        if (currentData.crop_pattern && currentData.crop_pattern.trim()) {
          payload.crop_pattern = currentData.crop_pattern.trim();
        }
      }

      if (currentData.is_non_agricultural) {
        if (currentData.na_category && currentData.na_category.trim()) {
          payload.na_category = currentData.na_category.trim();
        }
        if (currentData.na_purpose && currentData.na_purpose.trim()) {
          payload.na_purpose = currentData.na_purpose.trim();
        }
      }

      if (currentData.is_forest && currentData.forest_category && currentData.forest_category.trim()) {
        payload.forest_category = currentData.forest_category.trim();
      }

      if (currentData.is_government) {
        if (currentData.govt_department && currentData.govt_department.trim()) {
          payload.govt_department = currentData.govt_department.trim();
        }
        if (currentData.govt_purpose && currentData.govt_purpose.trim()) {
          payload.govt_purpose = currentData.govt_purpose.trim();
        }
      }

      if (currentData.is_water_body && currentData.water_body_type && currentData.water_body_type.trim()) {
        payload.water_body_type = currentData.water_body_type.trim();
      }

      // Add numeric fields
      if (currentData.land_rate_multiplier !== null && currentData.land_rate_multiplier !== '') {
        payload.land_rate_multiplier = parseFloat(currentData.land_rate_multiplier) || 1.0;
      }

      if (currentData.classification_level !== null && currentData.classification_level !== '') {
        payload.classification_level = parseInt(currentData.classification_level);
      }

      if (currentData.display_order !== null && currentData.display_order !== '') {
        payload.display_order = parseInt(currentData.display_order) || 0;
      }

      // Add other optional string fields if they have values
      if (currentData.development_potential && currentData.development_potential.trim()) {
        payload.development_potential = currentData.development_potential.trim();
      }

      if (currentData.transfer_restrictions && currentData.transfer_restrictions.trim()) {
        payload.transfer_restrictions = currentData.transfer_restrictions.trim();
      }

      if (currentData.description_en && currentData.description_en.trim()) {
        payload.description_en = currentData.description_en.trim();
      }

      if (currentData.description_mr && currentData.description_mr.trim()) {
        payload.description_mr = currentData.description_mr.trim();
      }

      if (currentData.area_unit_id) {
          payload.AREA_UNIT = currentData.area_unit_id;
        }

        if (currentData.act_id) {
          payload.legal_reference = currentData.act_id;
        }



      console.log('Sending payload:', JSON.stringify(payload, null, 2));

      if (formMode === 'edit') {
        await apiService.updateLandType(currentData.land_type_id, payload);
      } else {
        await apiService.addLandType(payload);
      }

      // Refresh the list
      const refreshed = await apiService.getLandTypes();
      setLandTypes(refreshed.data || []);
      setFormVisible(false);

      // Show success message
      alert(`Land type ${formMode === 'edit' ? 'updated' : 'added'} successfully!`);

    } catch (error) {
      console.error('Error saving land type:', error);

      // More detailed error handling
      let errorMessage = 'Failed to save land type: ';
      if (error.response) {
        errorMessage += error.response.data?.message || error.response.statusText || 'Server error';
      } else if (error.request) {
        errorMessage += 'Network error - please check your connection';
      } else {
        errorMessage += error.message || 'Unknown error';
      }

      alert(errorMessage);
    }
  };


  // Filter and search logic
  const filteredLandTypes = landTypes.filter((item) => {
    // Search term filter
    const matchesSearch =
      item.type_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type_name_en.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.type_name_mr.toLowerCase().includes(searchTerm.toLowerCase());

    // Active status filter
    const matchesActive =
      activeFilter === "all" ||
      (activeFilter === "active" && item.is_active) ||
      (activeFilter === "inactive" && !item.is_active);

    // Type filter
    const matchesType =
      typeFilter === "all" ||
      (typeFilter === "agricultural" && item.is_agricultural) ||
      (typeFilter === "nonAgricultural" && item.is_non_agricultural) ||
      (typeFilter === "forest" && item.is_forest) ||
      (typeFilter === "government" && item.is_government);

    return matchesSearch && matchesActive && matchesType;
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredLandTypes.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredLandTypes.length / itemsPerPage);

  // Function to change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-center items-center h-80">
          <div className="text-center animate-float">
            <div className="inline-flex relative">
              <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-purple-400 animate-pulse" />
              </div>
            </div>
            <p className="text-gray-400 mt-4">{translations[language].LoadingLandTypes}</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Search and Filter Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex flex-wrap gap-4">
          <div className="relative w-full md:w-80 group">
            <label className="block mb-1 text-sm font-medium text-gray-700">
              {translations[language].Search}
            </label>
            <input
              type="text"
              placeholder={translations[language].SearchProjects}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-700 placeholder-gray-400 
                focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 
                transition-all duration-200 shadow-sm group-hover:border-gray-300"
            />
            <div className="absolute left-3 top-10 text-gray-400 group-hover:text-gray-600 transition-colors duration-200">
              <Search className="h-5 w-5" />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 bottom-4 text-gray-400 hover:text-gray-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
          {/* <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">{translations[language].Status}</label>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <option value="all">{translations[language].All}</option>
              <option value="active">{translations[language].active}</option>
              <option value="inactive">{translations[language].inactive}</option>
            </select>
          </div> */}
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">{translations[language].Type}</label>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="all">{translations[language].All_Types}</option>
              <option value="agricultural">{translations[language].Agricultural}</option>
              <option value="nonAgricultural">{translations[language].NonAgricultural}</option>
              <option value="forest">{translations[language].Forest}</option>
              <option value="government">{translations[language].Government}</option>
            </select>
          </div>
          {/* Add download button */}
          <div className="mt-6">
            {/* Add download button */}
            <button
              onClick={handleDownload}
              className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            // disabled={filteredUsers.length === 0}
            >
              <Download className="mr-1 h-4 w-4" /> {translations[language].export}
            </button>
          </div>
          <div className="mt-8 md:mt-5">
            {/* Add download button */}
            {/* <button
                onClick={handleDownload}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                // disabled={filteredUsers.length === 0}
              >
                <Download className="mr-1 h-4 w-4" /> {translations[language].export}
              </button> */}
            <button
              // onClick={() => setShowCreateForm(true)}
              onClick={handleAdd}
              className="px-5 py-3 text-sm bg-gradient-to-r from-blue-600 to-gray-900 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl flex items-center space-x-2 transition-all duration-300 shadow-lg shadow-blue-200 hover:shadow-blue-300 group"
            >
              <Plus className="h-5 w-5 group-hover:rotate-90 transition-transform duration-300" />
              <span className="font-medium">{translations[language].Add_New_Land_Type}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto rounded-lg shadow border border-gray-200">
        <table className="min-w-full bg-white divide-y divide-gray-200">
          <thead className="bg-blue-50">
            <tr>
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {translations[language].CODE}
              </th>
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {translations[language].NAME_EN_MR}
              </th>
              {/* <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {translations[language].DESCRIPTION}
              </th> */}
              <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {translations[language].CATEGORY}
              </th>
              {/* <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {translations[language].PROPERTIES}
              </th> */}
              {/* <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {translations[language].STATUS}
              </th> */}
              <th className="py-3 px-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                {translations[language].ACTIONS}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentItems.length > 0 ? (
              currentItems.map((item) => (
                <tr
                  key={`landtype-${item.land_type_id || item.type_code}`}
                  className="hover:bg-gray-50"
                >
                  <td className="py-2 px-4 font-medium text-gray-700">
                    {item.type_code}
                  </td>
                  <td className="py-2 px-4">
                    <div className="text-gray-700">{item.type_name_en}</div>
                    <div className="text-sm text-gray-500">
                      {item.type_name_mr}
                    </div>
                  </td>
                  {/* <td className="py-2 px-4 text-sm text-gray-500 max-w-xs truncate">
                    {item.description_en}
                  </td> */}
                  <td className="py-2 px-4 text-sm">
                    <div className="flex flex-col space-y-1">
                      {item.is_agricultural && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {translations[language].Agricultural}
                        </span>
                      )}
                      {item.is_non_agricultural && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {translations[language].NonAgricultural}
                        </span>
                      )}
                      {item.is_forest && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                         {translations[language].Forest}
                        </span>
                      )}
                      {item.is_government && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {translations[language].Government}
                        </span>
                      )}
                    </div>
                  </td>
                  {/* <td className="py-2 px-4 text-sm text-center">
                    <div className="flex flex-wrap justify-center gap-1">
                      {item.is_gaothan && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Gaothan
                        </span>
                      )}
                      {item.is_tribal_area && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Tribal
                        </span>
                      )}
                      {item.is_heritage && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Heritage
                        </span>
                      )}
                    </div>
                  </td> */}
                  {/* <td className="py-2 px-4 text-center">
                    <label className="inline-flex items-center cursor-pointer">
                      
                      <input
                        type="checkbox"
                        checked={item.is_active}
                        onChange={() => toggleStatus(item.id)}
                        className="sr-only peer"
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </td> */}
                  <td className="py-2 px-4 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(item.land_type_id)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-4 text-center text-gray-500">
                  {translations[language].NoLandTypesFound}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredLandTypes.length > itemsPerPage && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-500">
            {translations[language].Showing} {indexOfFirstItem + 1} {translations[language].to}{" "}
            {Math.min(indexOfLastItem, filteredLandTypes.length)} {translations[language].of}{" "}
            {filteredLandTypes.length} {translations[language].Entries}
          </div>
          <div className="flex space-x-1">
            <button
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1 rounded ${currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
            >
              {translations[language].Previous}
            </button>
            {[...Array(totalPages).keys()].map((number) => (
              <button
                key={number + 1}
                onClick={() => paginate(number + 1)}
                className={`px-3 py-1 rounded ${currentPage === number + 1
                  ? "bg-blue-500 text-white"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
              >
                {number + 1}
              </button>
            ))}
            <button
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-3 py-1 rounded ${currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
            >
              {translations[language].Next}
            </button>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {formVisible && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 sm:p-6">
    <div className="relative w-full max-w-5xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
      <div className="p-6 sm:p-8">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-6">
          <h3 className="text-2xl font-semibold text-gray-800">
            {formMode === "edit" ? <span>{translations[language].EditLandType}</span> : <span>{translations[language].AddNewLandType}</span>}
          </h3>
          <button
            onClick={() => setFormVisible(false)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-6 w-6 text-gray-500 hover:text-gray-700" />
          </button>
        </div>

        <form onSubmit={handleFormSubmit} className="space-y-8">
          {/* Header Banner */}
          {/* <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h2 className="text-xl font-semibold text-blue-900">
              {formMode === "edit" ? "Edit" : "Add New"} {translations[language].landtype}
            </h2>
          </div> */}

          {/* Main Content */}
          <div className="space-y-8">
            {/* Basic Information Section */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="bg-gradient-to-r from-sky-400 to-sky-500 text-white p-4 rounded-t-lg">
                <h3 className="text-lg font-semibold flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {translations[language].BasicInformation}
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].TypeCode} *
                    </label>
                    <input
                      name="type_code"
                      value={currentData.type_code}
                      onChange={(e) => handleFormChange("type_code", e.target.value)}
                      required
                      maxLength={20}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter type code"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].NameEnglish} *
                    </label>
                    <input
                      name="type_name_en"
                      value={currentData.type_name_en}
                      onChange={(e) => handleFormChange("type_name_en", e.target.value)}
                      required
                      maxLength={100}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter name in English"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].NameMarathi}
                    </label>
                    <input
                      name="type_name_mr"
                      value={currentData.type_name_mr || ""}
                      onChange={(e) => handleFormChange("type_name_mr", e.target.value)}
                      maxLength={100}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter name in Marathi"
                    />
                  </div>
                  {/* <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Area Covered
                    </label>
                    <input
                      name="legal_reference"
                      value={currentData.legal_reference}
                      onChange={(e) => handleFormChange("legal_reference", e.target.value)}
                      maxLength={255}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter area covered"
                    />
                  </div> */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].DisplayOrder}
                    </label>
                    <input
                      type="number"
                      name="display_order"
                      value={currentData.display_order}
                      onChange={(e) => handleFormChange("display_order", parseInt(e.target.value))}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter display order"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].ClassificationLevel}
                    </label>
                    <input
                      type="number"
                      name="classification_level"
                      value={currentData.classification_level}
                      onChange={(e) => handleFormChange("classification_level", parseInt(e.target.value))}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter classification level"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].DescriptionEnglish}
                    </label>
                    <textarea
                      name="description_en"
                      value={currentData.description_en}
                      onChange={(e) => handleFormChange("description_en", e.target.value)}
                      maxLength={500}
                      rows={4}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter description in English"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].DescriptionMarathi}
                    </label>
                    <textarea
                      name="description_mr"
                      value={currentData.description_mr}
                      onChange={(e) => handleFormChange("description_mr", e.target.value)}
                      maxLength={500}
                      rows={4}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter description in Marathi"
                    />
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentData.is_active}
                      onChange={() => handleFormChange("is_active", !currentData.is_active)}
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-blue-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700">
                      {translations[language].ActiveStatus}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Land Classification Section */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="bg-gradient-to-r from-emerald-400 to-emerald-500 text-white p-4 rounded-t-lg">
                <h3 className="text-lg font-semibold flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  {translations[language].LandClassification}
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].AssessmentType}
                    </label>
                    <select
                      name="assessment_type"
                      value={currentData.assessment_type}
                      onChange={(e) => handleFormChange("assessment_type", e.target.value)}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">{translations[language].SelectType}</option>
                      <option value="Regular">{translations[language].Regular}</option>
                      <option value="Exempt">{translations[language].Exempt}</option>
                      <option value="Special">{translations[language].Special}</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].RevenueCategory}
                    </label>
                    <select
                      name="revenue_category"
                      value={currentData.revenue_category}
                      onChange={(e) => handleFormChange("revenue_category", e.target.value)}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">{translations[language].SelectCategory}</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                      <option value="E">E</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                     {translations[language].AreaUnit}
                    </label>
                    <select
                      name="area_unit_id"
                      value={currentData.area_unit_id || ""}
                      // onChange={(e) => handleFormChange("area_unit", e.target.value)}
                      onChange={(e) => handleFormChange("area_unit_id", parseInt(e.target.value))}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">{translations[language].SelectAreaUnit}</option>
                      {areaUnits.map((unit) => (
                      <option key={unit.area_unit_id} value={unit.area_unit_id}>
                        {unit.unit_name}
                      </option>
                    ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].LegalReference}
                    </label>
                    <select
                       name="act_id"
                       value={currentData.act_id || ""}
                      // onChange={(e) => handleFormChange("act_id", parseInt(e.target.value))}
                      onChange={(e) => handleFormChange("act_id", parseInt(e.target.value))}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">Select Act</option>
                      {acts.map((act) => (
                        <option key={act.act_id} value={act.act_id}>
                          {act.act_no}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].DevelopmentPotential}
                    </label>
                    <select
                      name="development_potential"
                      value={currentData.development_potential}
                      onChange={(e) => handleFormChange("development_potential", e.target.value)}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    >
                      <option value="">{translations[language].SelectPotential}</option>
                      <option value="High">{translations[language].High}</option>
                      <option value="Medium">{translations[language].medium}</option>
                      <option value="Low">{translations[language].Low}</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].TransferRestrictions}
                    </label>
                    <input
                      name="transfer_restrictions"
                      value={currentData.transfer_restrictions}
                      onChange={(e) => handleFormChange("transfer_restrictions", e.target.value)}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter transfer restrictions"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {translations[language].LandRateMultiplier}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="land_rate_multiplier"
                      value={currentData.land_rate_multiplier}
                      onChange={(e) => handleFormChange("land_rate_multiplier", parseFloat(e.target.value))}
                      className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Enter multiplier"
                    />
                  </div>
                </div>

                

                {/* Land Type Categories */}
<div className="mt-6">
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {/* Agricultural Land */}
    <div>
      {/* ...your existing Agricultural Land content... */}
      <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <input
                        type="checkbox"
                        id="is_agricultural"
                        checked={currentData.is_agricultural}
                        onChange={(e) => handleFormChange("is_agricultural", e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_agricultural" className="text-sm font-medium text-gray-700">
                        {translations[language].AgriculturalLand}
                      </label>
                    </div>
                    {currentData.is_agricultural && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {translations[language].IrrigationType}
                          </label>
                          <select
                            name="irrigation_type"
                            value={currentData.irrigation_type}
                            onChange={(e) => handleFormChange("irrigation_type", e.target.value)}
                            className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          >
                            <option value="">{translations[language].SelectType}</option>
                            <option value="Canal">{translations[language].Canal}</option>
                            <option value="Well">{translations[language].Well}</option>
                            <option value="Tube Well">{translations[language].TubeWell}</option>
                            <option value="Rainfed">{translations[language].Rainfed}</option>
                            <option value="Other">{translations[language].Other}</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                           {translations[language].CropPattern}
                          </label>
                          <select
                            name="crop_pattern"
                            value={currentData.crop_pattern}
                            onChange={(e) => handleFormChange("crop_pattern", e.target.value)}
                            className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          >
                            <option value="">{translations[language].SelectPattern}</option>
                            <option value="Seasonal">{translations[language].Seasonal}</option>
                            <option value="Annual">{translations[language].Annual}</option>
                            <option value="Perennial">{translations[language].Perennial}</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
    </div>

    {/* Non-Agricultural Land */}
    <div>
      {/* ...your existing Non-Agricultural Land content... */}
      <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <input
                        type="checkbox"
                        id="is_non_agricultural"
                        checked={currentData.is_non_agricultural}
                        onChange={(e) => handleFormChange("is_non_agricultural", e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_non_agricultural" className="text-sm font-medium text-gray-700">
                        {translations[language].NonAgriculturalLand}
                      </label>
                    </div>
                    {currentData.is_non_agricultural && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pl-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {translations[language].Category}
                          </label>
                          <input
                            name="na_category"
                            value={currentData.na_category}
                            onChange={(e) => handleFormChange("na_category", e.target.value)}
                            className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="Enter category"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {translations[language].Purpose}
                          </label>
                          <input
                            name="na_purpose"
                            value={currentData.na_purpose}
                            onChange={(e) => handleFormChange("na_purpose", e.target.value)}
                            className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="Enter purpose"
                          />
                        </div>
                        <div className="flex items-center space-x-3 mt-6">
                          <input
                            type="checkbox"
                            id="na_permission_required"
                            checked={currentData.na_permission_required}
                            onChange={(e) => handleFormChange("na_permission_required", e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label htmlFor="na_permission_required" className="text-sm font-medium text-gray-700">
                            Permission Required
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
    </div>

    {/* Forest Land */}
    <div>
      {/* ...your existing Forest Land content... */}
       <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <input
                        type="checkbox"
                        id="is_forest"
                        checked={currentData.is_forest}
                        onChange={(e) => handleFormChange("is_forest", e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_forest" className="text-sm font-medium text-gray-700">
                        {translations[language].ForestLand}
                      </label>
                    </div>
                    {currentData.is_forest && (
                      <div className="pl-6">
                        <div className="space-y-2 max-w-sm">
                          <label className="block text-sm font-medium text-gray-700">
                            {translations[language].ForestCategory}
                          </label>
                          <select
                            name="forest_category"
                            value={currentData.forest_category}
                            onChange={(e) => handleFormChange("forest_category", e.target.value)}
                            className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                          >
                            <option value="">{translations[language].SelectCategory}</option>
                            <option value="Protected">{translations[language].Protected}</option>
                            <option value="Reserved">{translations[language].Reserved}</option>
                            <option value="Private">{translations[language].Private}</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
    </div>

    {/* Government Land */}
    <div>
      {/* ...your existing Government Land content... */}
      <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <input
                        type="checkbox"
                        id="is_government"
                        checked={currentData.is_government}
                        onChange={(e) => handleFormChange("is_government", e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_government" className="text-sm font-medium text-gray-700">
                        {translations[language].GovernmentLand}
                      </label>
                    </div>
                    {currentData.is_government && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {translations[language].GovernmentDepartment}
                          </label>
                          <input
                            name="govt_department"
                            value={currentData.govt_department}
                            onChange={(e) => handleFormChange("govt_department", e.target.value)}
                            className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="Enter department"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {translations[language].GovernmentPurpose}
                          </label>
                          <input
                            name="govt_purpose"
                            value={currentData.govt_purpose}
                            onChange={(e) => handleFormChange("govt_purpose", e.target.value)}
                            className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            placeholder="Enter purpose"
                          />
                        </div>
                      </div>
                    )}
                  </div>
      
    </div>
  </div>
</div>

              </div>
            </div>

            {/* Additional Properties Section */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="bg-gradient-to-r from-neutral-400 to-neutral-500 text-white p-4 rounded-t-lg">
                <h3 className="text-lg font-semibold flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  {translations[language].AdditionalProperties}
                </h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_gaothan"
                      checked={currentData.is_gaothan}
                      onChange={(e) => handleFormChange("is_gaothan", e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_gaothan" className="text-sm font-medium text-gray-700">
                      {translations[language].GaothanArea}
                    </label>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center space-x-3 mb-3">
                      <input
                        type="checkbox"
                        id="is_water_body"
                        checked={currentData.is_water_body}
                        onChange={(e) => handleFormChange("is_water_body", e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_water_body" className="text-sm font-medium text-gray-700">
                        {translations[language].WaterBody}
                      </label>
                    </div>
                    {currentData.is_water_body && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {translations[language].WaterBodyType}
                        </label>
                        <select
                          name="water_body_type"
                          value={currentData.water_body_type}
                          onChange={(e) => handleFormChange("water_body_type", e.target.value)}
                          className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        >
                          <option value="">{translations[language].SelectType}</option>
                          <option value="River">{translations[language].River}</option>
                          <option value="Lake">{translations[language].Lake}</option>
                          <option value="Pond">{translations[language].Pond}</option>
                          <option value="Reservoir">{translations[language].Reservoir}</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_sez"
                      checked={currentData.is_sez}
                      onChange={(e) => handleFormChange("is_sez", e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_sez" className="text-sm font-medium text-gray-700">
                      {translations[language].SpecialEconomicZone}
                    </label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_tribal_area"
                      checked={currentData.is_tribal_area}
                      onChange={(e) => handleFormChange("is_tribal_area", e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_tribal_area" className="text-sm font-medium text-gray-700">
                      {translations[language].TribalArea}
                    </label>
                  </div>
                  {/* <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_industrial"
                      checked={currentData.is_industrial}
                      onChange={(e) => handleFormChange("is_industrial", e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_industrial" className="text-sm font-medium text-gray-700">
                      {translations[language].IndustrialArea}
                    </label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_residential"
                      checked={currentData.is_residential}
                      onChange={(e) => handleFormChange("is_residential", e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_residential" className="text-sm font-medium text-gray-700">
                      {translations[language].ResidentialArea}
                    </label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_commercial"
                      checked={currentData.is_commercial}
                      onChange={(e) => handleFormChange("is_commercial", e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_commercial" className="text-sm font-medium text-gray-700">
                      {translations[language].CommercialArea}
                    </label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="is_taxable"
                      checked={currentData.is_taxable}
                      onChange={(e) => handleFormChange("is_taxable", e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_taxable" className="text-sm font-medium text-gray-700">
                      {translations[language].Taxable}
                    </label>
                  </div> */}
                  
                </div>
                {/* <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {translations[language].AdditionalNotes}
                  </label>
                  <textarea
                    name="additional_notes"
                    value={currentData.additional_notes || ""}
                    onChange={(e) => handleFormChange("additional_notes", e.target.value)}
                    rows={4}
                    maxLength={1000}
                    className="w-full border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Enter any additional notes or special conditions"
                  />
                </div> */}
              </div>
            </div>

            
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => setFormVisible(false)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all"
            >
              {translations[language].Cancel}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center"
            >
              {loading && (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {formMode === <span>{translations[language].Edit}</span> ? <span>{translations[language].Update}</span>:<span>{translations[language].Save}</span>}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
)}
      {/* Download Modal */}
      <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        title={translations[language].LandTypeManagement}
        data={filteredLandTypes}
        columns={downloadColumns}
        isLoading={isDownloadLoading}
        setIsLoading={setIsDownloadLoading}
        screenName="Land Type Management"
      />
    </div>
  );
};

export default LandTypeManagement;